package com.flowyride.feature.planner.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.foundation.layout.Row
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInParent
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.LifecycleStartEffect
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.flowyride.core.fileexport.ExportableFile
import com.flowyride.core.fileexport.OpenedFiles
import com.flowyride.core.fileexport.PickedFile
import com.flowyride.core.fileexport.rememberFileExporter
import com.flowyride.core.fileexport.rememberFilePicker
import com.flowyride.core.domain.analysis.distanceTo
import com.flowyride.core.domain.analysis.speedStretches
import com.flowyride.core.domain.favorite.FavoriteSummary
import com.flowyride.core.domain.speedcamera.SpeedCamera
import com.flowyride.core.device.StatusBarIconsEffect
import com.flowyride.core.device.rememberLocationPermission
import com.flowyride.core.domain.gpx.GpxDocument
import com.flowyride.core.domain.util.suspendRunCatching
import com.flowyride.core.map.MapClick
import com.flowyride.core.map.CenterRequest
import com.flowyride.core.map.MapMarker
import com.flowyride.core.map.MapPlace
import com.flowyride.core.map.MapSpeedCamera
import com.flowyride.core.map.RouteMap
import com.flowyride.feature.planner.MapPickTarget
import com.flowyride.feature.planner.PlannerAction
import com.flowyride.feature.planner.PlannerEvent
import com.flowyride.feature.planner.PlannerMode
import com.flowyride.feature.planner.PlannerUiState
import com.flowyride.feature.planner.PlannerViewModel
import com.flowyride.feature.planner.RouteUiState
import com.flowyride.feature.planner.StopId
import com.flowyride.feature.planner.StopRole
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.error_export
import com.flowyride.feature.planner.resources.error_gpx_read
import com.flowyride.feature.planner.resources.error_location
import com.flowyride.feature.planner.ui.components.LocateButton
import com.flowyride.feature.planner.ui.components.SpeedCameraDialog
import com.flowyride.feature.planner.resources.favorite_rename
import com.flowyride.feature.planner.resources.favorite_save_title
import com.flowyride.feature.planner.resources.favorites_button
import com.flowyride.feature.planner.ui.components.FavoriteNameDialog
import com.flowyride.feature.planner.ui.components.FavoritesSheet
import com.flowyride.feature.planner.ui.components.GpxQuestionDialog
import com.flowyride.feature.planner.ui.components.ReplaceRideDialog
import com.flowyride.feature.planner.ui.components.SaveFavoriteDialog
import com.flowyride.feature.planner.ui.components.MapPickMenu
import com.flowyride.feature.planner.ui.components.PlaceNameDialog
import com.flowyride.feature.planner.ui.components.PlannerTopBar
import com.flowyride.feature.planner.ui.components.RideSheet
import com.flowyride.feature.planner.ui.components.SheetBody
import com.flowyride.feature.planner.ui.components.SheetDetent
import com.flowyride.feature.planner.ui.components.SheetHeader
import com.flowyride.feature.planner.resources.favorite_save
import com.flowyride.feature.planner.resources.save_place
import com.flowyride.core.domain.model.Place
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.only
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.getString
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

/** Room left under the "my position" button for the map attribution. */
private val AttributionClearance = 52.dp

/** Imported waypoints closer than this to the departure or arrival are not drawn. */
private const val WAYPOINT_AT_END_METERS = 100.0

/** Share of the screen height the destination suggestions may take before they scroll. */
private const val SuggestionsMaxHeightRatio = 0.4f

/** Above this share of the screen covered by the sheet, the "my position" button hides. */
private const val LocateButtonMaxSheetRatio = 0.6f

/** Map left visible above the fully open sheet. */
private val SheetTopGap = 8.dp

/** Stateful entry point: wires the [PlannerViewModel] and one-shot events. */
@Composable
fun PlannerRoute(
    viewModel: PlannerViewModel = koinViewModel(),
    openedFiles: OpenedFiles = koinInject(),
    accountButton: @Composable () -> Unit = {},
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val openedFile by openedFiles.pending.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val fileExporter = rememberFileExporter()
    var favoritesOpen by rememberSaveable { mutableStateOf(false) }

    LaunchedEffect(viewModel) {
        viewModel.events.collect { event ->
            when (event) {
                is PlannerEvent.SaveGpx -> suspendRunCatching { fileExporter.export(event.document.toExportableFile()) }
                    .onFailure { snackbarHostState.showSnackbar(getString(Res.string.error_export)) }
                is PlannerEvent.ShowError -> snackbarHostState.showSnackbar(getString(event.message))
                is PlannerEvent.ShowMessage -> snackbarHostState.showSnackbar(getString(event.message))
                PlannerEvent.FavoriteOpened -> favoritesOpen = false
            }
        }
    }

    PlannerScreen(
        state = state,
        onAction = viewModel::onAction,
        snackbarHostState = snackbarHostState,
        accountButton = accountButton,
        favoritesOpen = favoritesOpen,
        onFavoritesOpenChange = { open ->
            favoritesOpen = open
            if (open) viewModel.onAction(PlannerAction.LoadFavorites)
        },
        openedFile = openedFile,
        onOpenedFileTaken = openedFiles::consume,
    )
}

/** An action that replaces the ride on screen, waiting for the rider's go-ahead. */
private sealed interface Replacement {
    data object ImportGpx : Replacement
    data class OpenFavorite(val id: Long) : Replacement
}

/** Stateless screen: map in the background, controls on top (phone) or on the side (tablet / web). */
@Composable
fun PlannerScreen(
    state: PlannerUiState,
    onAction: (PlannerAction) -> Unit,
    snackbarHostState: SnackbarHostState = remember { SnackbarHostState() },
    accountButton: @Composable () -> Unit = {},
    favoritesOpen: Boolean = false,
    onFavoritesOpenChange: (Boolean) -> Unit = {},
    openedFile: Result<PickedFile>? = null,
    onOpenedFileTaken: (Result<PickedFile>) -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    val filePicker = rememberFilePicker(
        extensions = listOf("gpx"),
        onPicked = { onAction(PlannerAction.ImportGpx(it.fileName, it.content)) },
        onFailed = { scope.launch { snackbarHostState.showSnackbar(getString(Res.string.error_gpx_read)) } },
    )
    var pendingReplacement by remember { mutableStateOf<Replacement?>(null) }
    fun replace(replacement: Replacement) {
        pendingReplacement = null
        when (replacement) {
            Replacement.ImportGpx -> {
                onFavoritesOpenChange(false)
                filePicker.pick()
            }
            is Replacement.OpenFavorite -> onAction(PlannerAction.OpenFavorite(replacement.id))
        }
    }
    fun request(replacement: Replacement) {
        if (state.hasRide) pendingReplacement = replacement else replace(replacement)
    }
    LaunchedEffect(openedFile) {
        val file = openedFile ?: return@LaunchedEffect
        file.fold(
            // A ride on screen stays: the file goes to "Mes trajets".
            onSuccess = {
                if (state.hasRide) {
                    onAction(PlannerAction.SaveGpxToFavorites(it.fileName, it.content))
                } else {
                    onFavoritesOpenChange(false)
                    onAction(PlannerAction.ImportGpx(it.fileName, it.content))
                }
            },
            // Outlives this effect, cancelled as soon as the file is taken.
            onFailure = { scope.launch { snackbarHostState.showSnackbar(getString(Res.string.error_gpx_read)) } },
        )
        onOpenedFileTaken(file)
    }
    var savingFavorite by remember { mutableStateOf(false) }
    var renaming by remember { mutableStateOf<FavoriteSummary?>(null) }
    var savingPlace by remember { mutableStateOf<Place?>(null) }
    var detent by rememberSaveable { mutableStateOf(SheetDetent.COLLAPSED) }
    // A whole new ride (loop, favorite, file): the map first. The loop settings: open.
    LaunchedEffect(state.rideGeneration) { detent = SheetDetent.COLLAPSED }
    LaunchedEffect(state.mode) { if (state.mode == PlannerMode.LOOP) detent = SheetDetent.HALF }

    // Riding from "Ma position" needs the location: asked first, then the departure is filled (or left to the rider).
    var afterPermission by remember { mutableStateOf<(() -> Unit)?>(null) }
    val askLocationForDeparture = rememberLocationPermission { granted ->
        afterPermission?.invoke()
        afterPermission = null
        if (granted) onAction(PlannerAction.DepartureFromRider)
    }
    fun fromRider(action: () -> Unit) {
        if (state.stops.first().selected != null) {
            action()
        } else {
            afterPermission = action
            askLocationForDeparture()
        }
    }
    val onDestination: (Place) -> Unit = { place -> fromRider { onAction(PlannerAction.DestinationChosen(place)) } }
    val onLoop: () -> Unit = { fromRider { onAction(PlannerAction.ModeChanged(PlannerMode.LOOP)) } }

    val track = remember(state.route) {
        (state.route as? RouteUiState.Ready)?.route?.track?.map { it.point }.orEmpty()
    }
    val speedStretches = remember(state.route) { (state.route as? RouteUiState.Ready)?.route?.speedStretches().orEmpty() }
    val markers = remember(state.stops, state.imported) {
        state.stops.mapIndexedNotNull { index, stop ->
            val place = stop.selected ?: return@mapIndexedNotNull null
            val role = state.roleOf(index)
            val kind = when (role) {
                StopRole.DEPARTURE -> MapMarker.Kind.DEPARTURE
                StopRole.INTERMEDIATE -> MapMarker.Kind.STOP
                StopRole.ARRIVAL -> MapMarker.Kind.ARRIVAL
            }
            // Same numbering as the "Étape N" fields.
            MapMarker(
                stop.id.value.toString(),
                place.point,
                kind,
                label = index.toString().takeIf { role == StopRole.INTERMEDIATE },
                draggable = state.imported == null,
            )
        } + state.imported?.waypoints.orEmpty().mapIndexedNotNull { index, waypoint ->
            // A waypoint at an end would hide the departure or arrival marker.
            val ends = listOfNotNull(state.stops.first().selected?.point, state.stops.last().selected?.point)
            if (ends.any { it.distanceTo(waypoint.point) < WAYPOINT_AT_END_METERS }) return@mapIndexedNotNull null
            MapMarker("wpt-$index", waypoint.point, MapMarker.Kind.STOP, label = null, draggable = false)
        }
    }

    val speedCameraPoints = remember(state.speedCameras, state.showSpeedCameras) {
        if (state.showSpeedCameras) state.speedCameras.map { MapSpeedCamera(it.id, it.point) } else emptyList()
    }
    var tappedCamera by remember { mutableStateOf<SpeedCamera?>(null) }
    val askLocation = rememberLocationPermission { granted ->
        if (granted) {
            onAction(PlannerAction.LocateMe)
        } else {
            scope.launch { snackbarHostState.showSnackbar(getString(Res.string.error_location)) }
        }
    }
    // The map under the status bar is light: dark icons stay readable.
    StatusBarIconsEffect(darkIcons = true)
    // "You are here" while the planner is visible, if the location is already granted.
    LifecycleStartEffect(Unit) {
        onAction(PlannerAction.LocationTrackingChanged(active = true))
        onStopOrDispose { onAction(PlannerAction.LocationTrackingChanged(active = false)) }
    }

    BoxWithConstraints(Modifier.fillMaxSize()) {
        val density = LocalDensity.current
        var topOverlay by remember { mutableStateOf(0.dp) }
        var bottomOverlay by remember { mutableStateOf(0.dp) }
        var sideOverlay by remember { mutableStateOf(0.dp) }
        val containerHeight = maxHeight
        val wide = isWideLayout(maxWidth, maxHeight)

        var mapMenu by remember { mutableStateOf<MapClick?>(null) }
        RouteMap(
            track = track,
            speedStretches = speedStretches,
            markers = markers,
            speedCameras = speedCameraPoints,
            modifier = Modifier.fillMaxSize(),
            framingKey = state.rideGeneration,
            onSpeedCameraClick = { id -> tappedCamera = state.speedCameras.firstOrNull { it.id == id } },
            userLocation = state.userLocation,
            places = remember(state.savedPlaces) { state.savedPlaces.map { MapPlace(it.id, it.point, it.emoji, it.color) } },
            onPlaceClick = { id ->
                val place = state.savedPlaces.firstOrNull { it.id == id }?.toPlace()
                val emptyStop = state.stops.firstOrNull { it.selected == null }
                when {
                    place == null || state.imported != null -> {}
                    // Like a tap on the map, with the address's own name.
                    state.mode == PlannerMode.LOOP -> onAction(PlannerAction.PlaceSelected(state.stops.first().id, place))
                    !state.hasRide -> onDestination(place)
                    emptyStop != null -> onAction(PlannerAction.PlaceSelected(emptyStop.id, place))
                    else -> onAction(PlannerAction.DestinationChosen(place))
                }
            },
            centerRequest = state.locatedAt?.let { CenterRequest(it, state.locateGeneration) },
            // Loop mode: tap sets the departure. Route mode: tap fills the next empty field;
            // once everything is set, it offers the choices instead.
            onClick = { click ->
                if (state.imported != null) {
                    // Read-only line: nothing to pick.
                } else if (state.mode == PlannerMode.LOOP) {
                    onAction(PlannerAction.MapPointPicked(click.point, MapPickTarget.DEPARTURE))
                } else if (!state.hasRide) {
                    // Nothing planned: the first tap is where to ride, from "Ma position".
                    fromRider { onAction(PlannerAction.MapPointPicked(click.point, MapPickTarget.ARRIVAL)) }
                } else if (state.hasEmptyStop) {
                    onAction(PlannerAction.MapPointPicked(click.point, MapPickTarget.NEXT_EMPTY))
                } else {
                    mapMenu = click
                }
            },
            onLongClick = { if (state.imported == null) mapMenu = it },
            onMarkerMoved = { id, point -> id.toLongOrNull()?.let { onAction(PlannerAction.StopMovedOnMap(StopId(it), point)) } },
            onTrackDragged = if (state.imported == null) {
                { trackIndex, point -> onAction(PlannerAction.TrackDragged(trackIndex, point)) }
            } else {
                null
            },
            obscuredInsets = if (wide) {
                PaddingValues(start = sideOverlay)
            } else {
                PaddingValues(top = topOverlay, bottom = bottomOverlay)
            },
        )

        MapPickMenu(
            click = mapMenu,
            canAddStop = state.canAddStop,
            onPick = { click, target ->
                mapMenu = null
                onAction(PlannerAction.MapPointPicked(click.point, target))
            },
            onSavePlace = { click ->
                mapMenu = null
                savingPlace = Place(name = "", detail = null, point = click.point)
            },
            onDismiss = { mapMenu = null },
        )

        val topBar: @Composable (Modifier, @Composable () -> Unit) -> Unit = { modifier, account ->
            PlannerTopBar(
                state = state,
                onAction = { action ->
                    // The loop chip asks for the location first, like a destination.
                    if (action is PlannerAction.ModeChanged && action.mode == PlannerMode.LOOP) onLoop() else onAction(action)
                },
                onDestination = onDestination,
                onSummaryClick = { detent = if (detent == SheetDetent.COLLAPSED) SheetDetent.HALF else SheetDetent.COLLAPSED },
                onOpenFavorites = { onFavoritesOpenChange(true) },
                suggestionsMaxHeight = containerHeight * SuggestionsMaxHeightRatio,
                modifier = modifier,
                accountButton = account,
            )
        }
        val body: @Composable (Modifier) -> Unit = { modifier ->
            SheetBody(
                state = state,
                onAction = onAction,
                onSaveFavorite = { savingFavorite = true },
                onSavePlace = { savingPlace = it },
                modifier = modifier,
            )
        }
        if (wide) {
            PanelCard(
                Modifier.windowInsetsPadding(WindowInsets.safeDrawing).padding(12.dp)
                    .width(sidePanelWidth(maxWidth))
                    .fillMaxHeight()
                    .onGloballyPositioned { sideOverlay = with(density) { (it.positionInParent().x + it.size.width).toDp() } },
            ) {
                Column(
                    Modifier.verticalScroll(rememberScrollState()).padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        AppTitle(Modifier.weight(1f))
                        TextButton(onClick = { onFavoritesOpenChange(true) }) { Text(stringResource(Res.string.favorites_button)) }
                        accountButton()
                    }
                    topBar(Modifier, {})
                    SheetHeader(state, onAction)
                    body(Modifier)
                }
            }
        } else {
            topBar(
                Modifier
                    .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Top + WindowInsetsSides.Horizontal))
                    .padding(horizontal = 12.dp, vertical = 8.dp)
                    .fillMaxWidth()
                    .align(Alignment.TopCenter)
                    // Everything above the bar's bottom edge is hidden (status bar and margins included).
                    .onGloballyPositioned { topOverlay = with(density) { (it.positionInParent().y + it.size.height).toDp() } },
                accountButton,
            )
            RideSheet(
                detent = detent,
                onDetentChange = { detent = it },
                maxHeight = (containerHeight - topOverlay - SheetTopGap).coerceAtLeast(0.dp),
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .onGloballyPositioned { bottomOverlay = containerHeight - with(density) { it.positionInParent().y.toDp() } },
                header = { SheetHeader(state, onAction, Modifier.padding(start = 16.dp, end = 16.dp, bottom = 12.dp)) },
                content = { body(Modifier.padding(start = 12.dp, end = 12.dp, bottom = 16.dp)) },
            )
        }

        // Hidden once the sheet covers most of the map (whatever moved it: drag, scroll, tap).
        if (wide || bottomOverlay < containerHeight * LocateButtonMaxSheetRatio) {
            LocateButton(
                isLocating = state.isLocating,
                onClick = askLocation,
                // Above the map attribution, which sits in the bottom corner of the visible map.
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .then(if (wide) Modifier.windowInsetsPadding(WindowInsets.safeDrawing) else Modifier)
                    .padding(end = 16.dp, bottom = (if (wide) 0.dp else bottomOverlay) + AttributionClearance),
            )
        }
        tappedCamera?.let { camera -> SpeedCameraDialog(camera, onDismiss = { tappedCamera = null }) }

        if (favoritesOpen) {
            FavoritesSheet(
                state = state.favorites,
                onOpen = { request(Replacement.OpenFavorite(it.id)) },
                onRename = { renaming = it },
                onDelete = { onAction(PlannerAction.DeleteFavorite(it.id)) },
                onImportGpx = { request(Replacement.ImportGpx) },
                onRetry = { onAction(PlannerAction.LoadFavorites) },
                onDismiss = { onFavoritesOpenChange(false) },
            )
        }
        pendingReplacement?.let { replacement ->
            ReplaceRideDialog(onConfirm = { replace(replacement) }, onDismiss = { pendingReplacement = null })
        }
        state.gpxQuestion?.let { question ->
            GpxQuestionDialog(
                question = question,
                onTrackChosen = { onAction(PlannerAction.GpxTrackChosen(it)) },
                onComputeConfirmed = { onAction(PlannerAction.GpxPointsConfirmed) },
                onDismiss = { onAction(PlannerAction.GpxQuestionDismissed) },
            )
        }
        if (savingFavorite) {
            SaveFavoriteDialog(
                initialName = state.suggestedFavoriteName,
                title = stringResource(Res.string.favorite_save_title),
                onSave = {
                    savingFavorite = false
                    onAction(PlannerAction.SaveFavorite(it))
                },
                onDismiss = { savingFavorite = false },
            )
        }
        savingPlace?.let { place ->
            PlaceNameDialog(
                title = stringResource(Res.string.save_place),
                initialName = place.name,
                confirmLabel = stringResource(Res.string.favorite_save),
                onConfirm = {
                    savingPlace = null
                    onAction(PlannerAction.SavePlace(it, place))
                },
                onDismiss = { savingPlace = null },
            )
        }
        renaming?.let { favorite ->
            FavoriteNameDialog(
                title = stringResource(Res.string.favorite_rename),
                initialName = favorite.name,
                confirmLabel = stringResource(Res.string.favorite_rename),
                onConfirm = {
                    renaming = null
                    onAction(PlannerAction.RenameFavorite(favorite.id, it))
                },
                onDismiss = { renaming = null },
            )
        }

        SnackbarHost(
            snackbarHostState,
            Modifier.align(Alignment.BottomCenter).padding(bottom = bottomOverlay),
        )
    }
}

@Composable
private fun PanelCard(modifier: Modifier, content: @Composable () -> Unit) {
    Card(
        modifier = modifier,
        elevation = CardDefaults.cardElevation(defaultElevation = 6.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
    ) {
        Box { content() }
    }
}

@Composable
private fun AppTitle(modifier: Modifier = Modifier) {
    Text("FlowyRide", style = MaterialTheme.typography.headlineSmall, color = MaterialTheme.colorScheme.primary, modifier = modifier)
}

private fun GpxDocument.toExportableFile() = ExportableFile(fileName, GpxDocument.MIME_TYPE, content)

